/**
 * Signaling client — WebSocket connection to storage node for P2P key relay
 *
 * Connects to /api/signal on the storage service.  The server routes messages
 * between devices without inspecting payloads (zero-knowledge relay).
 *
 * Primary use-case: Device A has just been approved and wants to relay a
 * workspace seed directly to Device B (or vice-versa) rather than having the
 * seed sit on the auth server.  Seeds are RSA-OAEP encrypted — the relay server
 * can't decrypt them even if it tried.
 */

import { getOrCreateDeviceKey, unwrapSeed } from '@yada/crypto';
import { getLocalWrappedSeeds, storeLocalWrappedSeed } from './localSeedStorage.js';
import { getAccountWorkspaceInventory, getWrappedSeeds, getStorageUrl, getRelayUrl, getAccessToken, refreshAccessToken, setAccessToken, clearAccessToken, AuthServiceUnavailableError } from '@yada/api-client';


// ── Types ──────────────────────────────────────────────────────────────────────

export interface SeedRelayPayload {
  workspace_id: string;
  wrapped_seed: string; // base64-encoded RSA-OAEP blob
  kid: string;
  wrap_algorithm: string;
}

export interface SeedReadyPayload {
  from_device_id: string;
}

export interface PairOfferSeed {
  workspace_id: string;
  wrapped_seed: string; // base64 wrapped blob, encrypted with Device B's ephemeral public key
  wrap_algorithm: string;
}

export interface PairOfferPayload {
  seeds: PairOfferSeed[];
  wrapped_auth?: string; // base64 wrapped auth JSON bundle
}

export interface CollabUpdateMessage {
  type: 'collab:update';
  workspaceId: string;
  documentId: string;
  blob: string;
  from?: string;
  accountId?: string;
}

export interface SignalingMessage {
  type: 'seed:relay' | 'seed:ready' | 'pair:offer' | 'pair:complete' | 'pair:missed' | 'collab:update';
  to?: string;
  from?: string;
  accountId?: string;
  payload?: SeedRelayPayload | SeedReadyPayload | PairOfferPayload | Record<string, never>;
  workspaceId?: string;
  documentId?: string;
  blob?: string;
}

export type CollabUpdateHandler = (workspaceId: string, documentId: string, blob: string, fromDeviceId?: string) => void;

type SeedRelayHandler = (workspaceId: string, seed: ArrayBuffer) => void;
type SeedReadyHandler = (fromDeviceId: string) => void;
export interface PairOfferContext { sessionId: string; offerId: string; fromDeviceId: string; accountId: string }
export interface PairReceiptContext { sessionId: string; offerId: string; fromDeviceId: string }
type PairOfferHandler = (seeds: PairOfferSeed[], fromDeviceId: string, wrappedAuth: string | undefined, context: PairOfferContext) => void;
type PairCompleteHandler = (receipt: PairReceiptContext) => void;
const expectedReceipts = new Map<string, { offerId: string; receiptToken: string; targetDeviceId: string }>();
const subscriptionWaiters = new Map<string, { resolve: () => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> }>();
let subscribedSessionId: string | null = null;

// ── Internal state ─────────────────────────────────────────────────────────────

let ws: WebSocket | null = null;
let deviceId: string | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectDelayMs = 1_000;
const MAX_RECONNECT_MS = 30_000;
let isStarted = false;
let connectionReady = false;
let lastConnectionFailure: string | null = null;

const seedRelayHandlers = new Set<SeedRelayHandler>();
const seedReadyHandlers = new Set<SeedReadyHandler>();
const pairOfferHandlers = new Set<PairOfferHandler>();
const pairCompleteHandlers = new Set<PairCompleteHandler>();
type PairMissedHandler = (sessionId: string) => void;
const pairMissedHandlers = new Set<PairMissedHandler>();
const collabUpdateHandlers = new Set<CollabUpdateHandler>();

/** Callbacks to fire when the WS first opens (or re-opens after reconnect). */
const connectCallbacks: Array<() => void> = [];
/** Active pairing session subscription to automatically send whenever WS is open. */
let activePairingSessionId: string | null = null;

// ── Public API ─────────────────────────────────────────────────────────────────

/**
 * Start the signaling WebSocket connection.
 * Safe to call multiple times — idempotent while connected.
 */
/** Retire an older attempt before retry preparation or dialog cancellation. */
export function cancelPairOffer(sessionId: string): void { expectedReceipts.delete(sessionId); }

export async function startSignaling(): Promise<void> {
  if (isStarted && connectionReady && ws?.readyState === WebSocket.OPEN) return;
  isStarted = true;
  const key = await getOrCreateDeviceKey();
  deviceId = key.deviceId;
  await connect();
  try {
    await waitForConnection(4_000);
  } catch {
    // Non-fatal if server connection takes longer; background reconnect will keep retrying
  }
}

/** Stop the signaling connection. */
export function stopSignaling(): void {
  isStarted = false;
  connectionReady = false;
  lastConnectionFailure = null;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  ws?.close();
  ws = null;
  deviceId = null;
  reconnectDelayMs = 1_000;
  connectCallbacks.length = 0;
  activePairingSessionId = null;
  subscribedSessionId = null;
  expectedReceipts.clear();
  for (const pending of subscriptionWaiters.values()) { clearTimeout(pending.timer); pending.reject(new Error('Pairing connection stopped')); }
  subscriptionWaiters.clear();
}


/**
 * Resolve after the relay acknowledges authenticated or anonymous readiness, or reject after `timeoutMs`.
 * Safe to call multiple times — resolves immediately if already connected.
 */
export function waitForConnection(timeoutMs = 8_000): Promise<void> {
  if (connectionReady && ws?.readyState === WebSocket.OPEN) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const ready = () => { clearTimeout(timer); resolve(); };
    const timer = setTimeout(() => {
      const idx = connectCallbacks.indexOf(ready);
      if (idx >= 0) connectCallbacks.splice(idx, 1);
      reject(new Error(lastConnectionFailure
        ? `Could not connect to the signaling server: ${lastConnectionFailure}`
        : 'Could not connect to the signaling server. Check your network and try again.'));
    }, timeoutMs);
    connectCallbacks.push(ready);
  });
}

/**
 * Subscribe to pair:missed messages (Device A side).
 * Fires when the pair:offer was delivered to the server but Device B's session
 * wasn't subscribed — Device B may not have connected yet.
 */
export function onPairMissed(cb: PairMissedHandler): () => void {
  pairMissedHandlers.add(cb);
  return () => pairMissedHandlers.delete(cb);
}

// ── Token resolution ──────────────────────────────────────────────────────────

/**
 * Resolve a valid access token, attempting a refresh if missing/expired.
 * Mirrors the same logic used by syncPushClient so both connections get a
 * token through the same reliable path (not raw localStorage reads that can
 * race with the login flow storing the token for the first time).
 */
async function resolveToken(): Promise<string | null> {
  const token = getAccessToken();
  if (token) return token;
  try {
    const refreshed = await refreshAccessToken();
    setAccessToken(refreshed.access_token, refreshed.expires_in);
    return refreshed.access_token;
  } catch (err) {
    // An outage or lost network says nothing about the session; retry later.
    if (err instanceof AuthServiceUnavailableError || err instanceof TypeError) throw err;
    clearAccessToken();
    return null;
  }
}

/**
 * Send a wrapped seed directly to another device (P2P relay path).
 * Returns true if the message was dispatched (device may still be offline
 * — the server will drop it if the target isn't connected).
 */
export function relayWrappedSeed(
  targetDeviceId: string,
  payload: SeedRelayPayload
): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN || !connectionReady || !deviceId) return false;
  const msg: SignalingMessage = {
    type: 'seed:relay',
    to: targetDeviceId,
    from: deviceId,
    payload,
  };
  ws.send(JSON.stringify(msg));
  return true;
}

/**
 * Notify another device that seeds have been stored on the auth server
 * (fallback path — device was offline during relay attempt).
 */
export function notifySeedReady(targetDeviceId: string): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN || !connectionReady || !deviceId) return false;
  const msg: SignalingMessage = {
    type: 'seed:ready',
    to: targetDeviceId,
    from: deviceId,
    payload: { from_device_id: deviceId },
  };
  ws.send(JSON.stringify(msg));
  return true;
}

/**
 * Subscribe to seed:relay messages (direct seed delivery from another device).
 * The seed has already been decrypted before the callback fires.
 */
export function onSeedRelay(cb: SeedRelayHandler): () => void {
  seedRelayHandlers.add(cb);
  return () => seedRelayHandlers.delete(cb);
}

/**
 * Subscribe to seed:ready messages (another device stored seeds on auth server).
 */
export function onSeedReady(cb: SeedReadyHandler): () => void {
  seedReadyHandlers.add(cb);
  return () => seedReadyHandlers.delete(cb);
}

/** Maximum payload size for ephemeral WebSocket collab updates (512 KB). Larger updates rely on durable HTTP sync. */
export const MAX_COLLAB_UPDATE_BYTES = 512 * 1024;

/**
 * Send an ephemeral encrypted Yjs collab update to other online devices on the account.
 * Zero-knowledge and stateless: dropped if no other devices are listening or if update exceeds safe relay size.
 */
export function sendCollabUpdate(
  workspaceId: string,
  documentId: string,
  blob: string
): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN || !connectionReady || !deviceId) return false;
  if (!blob || blob.length > MAX_COLLAB_UPDATE_BYTES) return false;
  const msg: SignalingMessage = {
    type: 'collab:update',
    workspaceId,
    documentId,
    blob,
    from: deviceId,
  };
  ws.send(JSON.stringify(msg));
  return true;
}

/**
 * Subscribe to ephemeral collab:update broadcasts from other online devices.
 */
export function onCollabUpdate(cb: CollabUpdateHandler): () => void {
  collabUpdateHandlers.add(cb);
  return () => collabUpdateHandlers.delete(cb);
}

/**
 * Register this device as listening for a pairing session (Device B side).
 * The server will route pair:offer messages addressed to this sessionId here.
 * If the WS isn't open yet, queues the subscription for when it opens.
 */
export async function subscribePairingSession(sessionId: string): Promise<void> {
  activePairingSessionId = sessionId;
  await waitForConnection();
  if (subscribedSessionId === sessionId) return;
  return new Promise((resolve, reject) => {
    const previous = subscriptionWaiters.get(sessionId);
    if (previous) { clearTimeout(previous.timer); previous.reject(new Error('Pairing subscription renewed')); }
    const timer = setTimeout(() => {
      subscriptionWaiters.delete(sessionId);
      reject(new Error('The relay did not confirm the pairing session. Retry to create a usable code.'));
    }, 8000);
    subscriptionWaiters.set(sessionId, { resolve, reject, timer });
    ws?.send(JSON.stringify({ type: 'pair:subscribe', sessionId }));
  });
}

/**
 * Send wrapped workspace seeds to Device B via its sessionId (Device A side).
 * Returns true if the message was dispatched (Device B may still be offline).
 */
export function sendPairOffer(sessionId: string, seeds: PairOfferSeed[], wrappedAuth?: string, context?: { offerId: string; receiptToken: string; targetDeviceId: string }): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN || !connectionReady || !deviceId) return false;
  if (!context || !wrappedAuth) return false;
  expectedReceipts.set(sessionId, context);
  ws.send(JSON.stringify({
    type: 'pair:offer',
    offerId: context.offerId,
    to: sessionId,
    from: deviceId,
    payload: { seeds, wrapped_auth: wrappedAuth },
  }));
  return true;
}

/**
 * Confirm pairing is complete back to Device A (Device B side).
 * `targetDeviceId` is the device_id received in the pair:offer `from` field.
 */
export function sendPairComplete(targetDeviceId: string, context: PairOfferContext & { receiptToken: string }): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN || !connectionReady || !deviceId || context.sessionId !== activePairingSessionId) return false;
  ws.send(JSON.stringify({ type: 'pair:complete', to: targetDeviceId, from: deviceId,
    sessionId: context.sessionId, offerId: context.offerId, payload: { receiptToken: context.receiptToken } }));
  return true;
}

/**
 * Subscribe to incoming pair:offer messages (Device B side).
 * The callback receives the wrapped seeds and Device A's device_id.
 */
export function onPairOffer(cb: PairOfferHandler): () => void {
  pairOfferHandlers.add(cb);
  return () => pairOfferHandlers.delete(cb);
}

/**
 * Subscribe to pair:complete confirmation (Device A side).
 */
export function onPairComplete(cb: PairCompleteHandler): () => void {
  pairCompleteHandlers.add(cb);
  return () => pairCompleteHandlers.delete(cb);
}

/** Return true if the WebSocket is currently open. */
export function isSignalingConnected(): boolean {
  return connectionReady && ws?.readyState === WebSocket.OPEN;
}

// ── Connection logic ──────────────────────────────────────────────────────────

function buildSignalUrl(): string {
  const relayUrl = getRelayUrl() || getStorageUrl();
  const base = relayUrl.replace(/^http/, 'ws');
  const params = new URLSearchParams();
  // Bearer credentials are sent in the first frame, never in proxy/request URLs.
  if (deviceId) params.set('device_id', deviceId);
  return `${base}/api/signal?${params}`;
}

// The socket only exists after the token resolves, so concurrent callers share
// this attempt instead of each opening (and leaking) a socket.
let connecting: Promise<void> | null = null;

function connect(): Promise<void> {
  if (!connecting) connecting = openConnection().finally(() => { connecting = null; });
  return connecting;
}

async function openConnection(): Promise<void> {
  if (!isStarted || !deviceId) return;
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return;

  // Resolve a valid token if available (relay only strictly requires device_id)
  let token: string | null = null;
  try {
    token = await resolveToken();
  } catch {
    // Opening without identity now would leave an approved device unidentified
    // until the next reconnect; wait for the auth service instead.
    scheduleReconnect();
    return;
  }
  if (!isStarted) return;

  try {
    const connection = new WebSocket(buildSignalUrl());
    ws = connection;
    connectionReady = false;
    lastConnectionFailure = null;

    ws.onopen = () => {
      if (ws !== connection || !isStarted) return;
      if (token && ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'auth:identify', token }));
      console.log('[Signaling] Connected');
      reconnectDelayMs = 1_000;
      if (!token) markConnectionReady();
    };

    ws.onclose = (event) => {
      if (ws !== connection) return;
      connectionReady = false;
      lastConnectionFailure = event.reason || `WebSocket closed with code ${event.code}`;
      ws = null;
      subscribedSessionId = null;
      // Another authenticated tab has taken over this device's relay slot.
      // Reconnecting here would repeatedly evict the active pairing tab.
      if (event.code === 4009) { console.log('[Signaling] Device session moved to another tab'); isStarted = false; return; }
      console.log('[Signaling] Disconnected — scheduling reconnect');
      scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose fires after onerror, so just log here
      lastConnectionFailure = 'WebSocket connection failed';
      console.warn('[Signaling] WebSocket error');
    };

    ws.onmessage = (event) => {
      if (ws !== connection || !isStarted) return;
      handleMessage(event.data as string);
    };
  } catch (err) {
    console.warn('[Signaling] Failed to open WebSocket:', err);
    scheduleReconnect();
  }
}

function markConnectionReady(): void {
  if (connectionReady || !ws || ws.readyState !== WebSocket.OPEN) return;
  connectionReady = true;
  lastConnectionFailure = null;
  connectCallbacks.splice(0).forEach(callback => callback());
  if (activePairingSessionId) ws.send(JSON.stringify({ type: 'pair:subscribe', sessionId: activePairingSessionId }));
}

function scheduleReconnect(): void {
  if (!isStarted || reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect().catch(err => console.warn('[Signaling] Reconnect error:', err));
  }, reconnectDelayMs);
  reconnectDelayMs = Math.min(reconnectDelayMs * 2, MAX_RECONNECT_MS);
}

// ── Message handling ──────────────────────────────────────────────────────────

function handleMessage(raw: string): void {
  let msg: SignalingMessage & { sessionId?: string; offerId?: string; accountId?: string };
  try {
    msg = JSON.parse(raw);
  } catch {
    console.warn('[Signaling] Malformed message:', raw);
    return;
  }

  if (!msg || typeof msg !== 'object') return;
  switch (msg.type as string) {
    case 'auth:identified':
    case 'auth:anonymous': markConnectionReady(); break;
    case 'pair:subscribed': {
      if (msg.sessionId !== activePairingSessionId) return;
      subscribedSessionId = msg.sessionId ?? null;
      const pending = subscriptionWaiters.get(msg.sessionId!);
      if (pending) { clearTimeout(pending.timer); subscriptionWaiters.delete(msg.sessionId!); pending.resolve(); }
      break;
    }
    case 'seed:relay':
      handleSeedRelay(msg).catch(err =>
        console.error('[Signaling] Error handling seed:relay:', err)
      );
      break;
    case 'seed:ready': {
      const p = msg.payload as SeedReadyPayload;
      seedReadyHandlers.forEach(cb => {
        try { cb(p.from_device_id); } catch { /* ignore */ }
      });
      break;
    }
    case 'pair:offer': {
      if (msg.sessionId !== activePairingSessionId || !msg.offerId || !msg.accountId || !msg.from) return;
      const p = msg.payload as PairOfferPayload;
      if (!p || !Array.isArray(p.seeds) || typeof p.wrapped_auth !== 'string') return;
      const context = { sessionId: msg.sessionId, offerId: msg.offerId, accountId: msg.accountId, fromDeviceId: msg.from };
      const from = (msg as { from?: string }).from ?? '';
      pairOfferHandlers.forEach(cb => {
        try { cb(p.seeds, from, p.wrapped_auth, context); } catch { /* ignore */ }
      });
      break;
    }
    case 'pair:complete': {
      if (!msg.sessionId || !msg.offerId || !msg.from) return;
      const expected = expectedReceipts.get(msg.sessionId);
      const token = (msg.payload as { receiptToken?: string })?.receiptToken;
      if (!expected || expected.offerId !== msg.offerId || expected.targetDeviceId !== msg.from || expected.receiptToken !== token) return;
      expectedReceipts.delete(msg.sessionId);
      const receipt = { sessionId: msg.sessionId, offerId: msg.offerId, fromDeviceId: msg.from };
      pairCompleteHandlers.forEach(cb => {
        try { cb(receipt); } catch { /* ignore */ }
      });
      break;
    }
    case 'collab:update': {
      if (typeof msg.workspaceId !== 'string' || typeof msg.documentId !== 'string' || typeof msg.blob !== 'string') return;
      const workspaceId = msg.workspaceId;
      const documentId = msg.documentId;
      const blob = msg.blob;
      const from = (msg as { from?: string }).from;
      collabUpdateHandlers.forEach(cb => {
        try { cb(workspaceId, documentId, blob, from); } catch { /* ignore */ }
      });
      break;
    }
    case 'pair:missed': {
      const sessionId = (msg as { to?: string }).to ?? '';
      pairMissedHandlers.forEach(cb => {
        try { cb(sessionId); } catch { /* ignore */ }
      });
      break;
    }
    default:
      console.warn('[Signaling] Unknown message type:', (msg as { type: string }).type);
  }
}

async function handleSeedRelay(msg: SignalingMessage): Promise<void> {
  const p = msg.payload as SeedRelayPayload;
  if (!p || typeof p.workspace_id !== 'string' || typeof p.wrapped_seed !== 'string' || !msg.from) return;
  const { captureDeviceSyncScope } = await import('./workspaceHydration.js');
  const capture = await captureDeviceSyncScope();
  if (msg.accountId !== capture.scope.accountId) throw new Error('Relayed key belongs to another account');
  const inventory = await getAccountWorkspaceInventory();
  await capture.assertCurrent();
  if (inventory.accountId !== capture.scope.accountId) throw new Error('Relayed key inventory changed');
  const workspace = inventory.workspaces.find(item => item.id === p.workspace_id && item.status === 'active' && item.encrypted);
  if (!workspace) throw new Error('Relayed key is outside active encrypted workspace access');
  // Decode the wrapped seed
  const wrappedSeedBuffer = Uint8Array.from(
    atob(p.wrapped_seed),
    c => c.charCodeAt(0)
  ).buffer;

  // Unwrap with our device private key
  const key = await getOrCreateDeviceKey();
  let seed: ArrayBuffer;
  try {
    seed = await unwrapSeed(wrappedSeedBuffer, key.privateKey);
  } catch (err) {
    console.error('[Signaling] Failed to unwrap relayed seed for workspace', p.workspace_id, err);
    return;
  }

  if (seed.byteLength !== 32) throw new Error('Invalid relayed key length');
  const localCopies = await getLocalWrappedSeeds(p.workspace_id, { device_id: key.deviceId, wrap_type: 'device' });
  const serverCopies = await getWrappedSeeds(p.workspace_id, { device_id: key.deviceId, wrap_type: 'device' });
  for (const previous of [...localCopies, ...serverCopies]) {
    if ('account_id' in previous && previous.account_id && previous.account_id !== capture.scope.accountId) throw new Error('Existing workspace key belongs to another account');
    const previousBytes = Uint8Array.from(atob(previous.wrapped_seed), c => c.charCodeAt(0)).buffer;
    const existing = previous.wrap_algorithm === 'raw' ? previousBytes : await unwrapSeed(previousBytes, key.privateKey);
    const bytes = new Uint8Array(seed), original = new Uint8Array(existing);
    if (bytes.length !== original.length || bytes.some((byte, index) => byte !== original[index])) throw new Error('Cannot replace an existing workspace encryption key via relay');
  }
  await capture.assertCurrent();
  const kid = workspace.kid || p.kid;
  const previous = localCopies.find(copy => copy.profile_id === workspace.profileId && copy.kid === kid);
  await storeLocalWrappedSeed({ account_id: capture.scope.accountId, workspace_id: p.workspace_id, profile_id: workspace.profileId, device_id: key.deviceId,
    kid, wrap_type: 'device', wrapped_seed: previous?.wrapped_seed || p.wrapped_seed,
    wrap_algorithm: previous?.wrap_algorithm || (key.publicKey.algorithm.name === 'X25519' ? 'X25519-AES-GCM' : 'RSA-OAEP-256'), created_at: previous?.created_at || new Date().toISOString() });

  console.log('[Signaling] Received and decrypted seed for workspace', p.workspace_id);

  // Notify listeners (storage layer will cache and unlock)
  seedRelayHandlers.forEach(cb => {
    try { cb(p.workspace_id, seed); } catch { /* ignore */ }
  });

  // Also dispatch a DOM event so locked storage can react
  window.dispatchEvent(
    new CustomEvent('yada:workspace:seed-received', { detail: { workspaceId: p.workspace_id } })
  );
}
